/**
 * The single adapter from the `GET /api/dispatch/board` wire shape (#2435) to
 * the board view model. This is the only module that names the generated
 * response type; the board UI consumes `BoardDay` (ADR-0006: the contract is
 * generated, never copied by hand).
 */
import type { components } from '@/types/api.generated';
import type { BoardDay, BoardGateKind, BoardStage } from './boardModel';

export type DeliveryFlowWire = components['schemas']['DeliveryFlow'];
type WireColumn = DeliveryFlowWire['columns'][number];

export type BoardWire = components['schemas']['DispatchBoard'];
type WireRow = BoardWire['rows'][number];
type WireCounts = WireRow['counts'];
type WireGateKind = NonNullable<WireRow['next_gate']>['kind'];

// Every board column reads one wire count ...
const WIRE_COUNT_KEY = {
  awaiting_dispatch: 'awaiting_dispatch',
  awaiting_assignment: 'awaiting_assignment',
  awaiting_departure: 'awaiting_departure',
  en_route: 'en_route',
  delivered: 'delivered',
  exception_or_cancelled: 'exception_or_cancelled',
} as const satisfies Record<BoardStage, keyof WireCounts>;
// ... and every wire count is read by a column: a count the wire adds without
// a board column fails to compile here.
type UnmappedWireCount = Exclude<keyof WireCounts, (typeof WIRE_COUNT_KEY)[BoardStage]>;
true satisfies [UnmappedWireCount] extends [never] ? true : never;

// Every wire gate kind has a board gate ...
const WIRE_GATE_KIND = {
  packaging_cutoff: 'packaging_cutoff',
  dispatch_cutoff: 'dispatch_cutoff',
  delivery_window_end: 'delivery_window_end',
} as const satisfies Record<WireGateKind, BoardGateKind>;
// ... and every board gate is one the wire can send, so no label is dead.
type UnsentBoardGate = Exclude<BoardGateKind, (typeof WIRE_GATE_KIND)[WireGateKind]>;
true satisfies [UnsentBoardGate] extends [never] ? true : never;

// Every wire column is a board stage; the compiler checks the map is total.
const WIRE_COLUMN_STAGE = {
  awaiting_dispatch: 'awaiting_dispatch',
  awaiting_assignment: 'awaiting_assignment',
  awaiting_departure: 'awaiting_departure',
  en_route: 'en_route',
  delivered: 'delivered',
  exception_or_cancelled: 'exception_or_cancelled',
} as const satisfies Record<WireColumn, BoardStage>;
// ... and every board stage is a column the wire can send.
type UnsentBoardStage = Exclude<BoardStage, (typeof WIRE_COLUMN_STAGE)[WireColumn]>;
true satisfies [UnsentBoardStage] extends [never] ? true : never;

export function toBoardStages(columns: DeliveryFlowWire['columns']): BoardStage[] {
  return columns.map((column) => WIRE_COLUMN_STAGE[column]);
}

export function toBoardStage(column: WireColumn): BoardStage {
  return WIRE_COLUMN_STAGE[column];
}

export function toBoardDays(wire: BoardWire): BoardDay[] {
  return wire.rows.map((row) => ({
    date: row.shipping_date,
    counts: {
      awaiting_dispatch: row.counts[WIRE_COUNT_KEY.awaiting_dispatch],
      awaiting_assignment: row.counts[WIRE_COUNT_KEY.awaiting_assignment],
      awaiting_departure: row.counts[WIRE_COUNT_KEY.awaiting_departure],
      en_route: row.counts[WIRE_COUNT_KEY.en_route],
      delivered: row.counts[WIRE_COUNT_KEY.delivered],
      exception_or_cancelled: row.counts[WIRE_COUNT_KEY.exception_or_cancelled],
    },
    total: row.total,
    restDay: row.rest_day,
    // `local_time` is the merchant wall clock (HH:mm), shown as is; `at` is
    // the same instant as a UTC ISO string.
    nextGate: row.next_gate
      ? {
          kind: WIRE_GATE_KIND[row.next_gate.kind],
          localTime: row.next_gate.local_time,
          atIso: row.next_gate.at,
        }
      : null,
    signals: {
      preflightAnomalies: row.signals.preflight_anomalies,
      unassignedTasks: row.signals.unassigned_tasks,
      deliveredLate: row.signals.delivered_late,
    },
  }));
}
